import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {
  fetchCreditBalance,
  fetchCreditTransactions,
  fetchAvailableFeatures,
  checkFeatureAccess,
  useFeature as consumeFeatureAction,
  addAlert,
  markAlertAsRead,
  markAllAlertsAsRead,
  removeAlert,
  clearAlerts,
  updateBalance,
  clearError,
  checkLowBalance,
  checkExpiringCredits,
  CreditAlert,
} from '../store/slices/creditsSlice';

export const useCredits = () => {
  const dispatch = useDispatch<AppDispatch>();
  const creditsState = useSelector((state: RootState) => state.credits);

  // Auth check — only run side-effects when the user is logged in
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const hasToken = !!(
    useSelector((state: RootState) => state.auth.token) ||
    localStorage.getItem('token')
  );

  // NO POLLING — credit balance is refreshed via notifyCreditChange() events
  // dispatched by components after credit-consuming actions succeed.

  // Check for low balance and expiring credits when balance updates
  useEffect(() => {
    if (creditsState.balance && (isAuthenticated || hasToken)) {
      dispatch(checkLowBalance());
      dispatch(checkExpiringCredits());
    }
  }, [dispatch, creditsState.balance, isAuthenticated, hasToken]);

  // Initialize credits data (call once on mount of dashboard / credits page)
  const initializeCredits = useCallback(async () => {
    try {
      await dispatch(fetchAvailableFeatures()).unwrap();
    } catch (error) {
      console.error('Failed to initialize credits:', error);
    }
  }, [dispatch]);

  // Refresh just available features
  const refreshCredits = useCallback(async () => {
  }, []);

  // Refresh credit features and transactions
  const refreshAllCredits = useCallback(async () => {
    try {
      await Promise.all([
        dispatch(fetchCreditTransactions({})).unwrap(),
        dispatch(fetchAvailableFeatures()).unwrap(),
      ]);
    } catch (error) {
      console.error('Failed to refresh all credits:', error);
    }
  }, [dispatch]);

  // Check if user can access a feature
  const canAccessFeature = useCallback(
    async (featureKey: string): Promise<boolean> => {
      try {
        const result = await dispatch(checkFeatureAccess(featureKey)).unwrap();
        return result.has_access;
      } catch (error) {
        console.error('Failed to check feature access:', error);
        return false;
      }
    },
    [dispatch]
  );

  // Use a feature (deduct credits)
  const consumeFeature = useCallback(
    async (featureKey: string, usageData?: any): Promise<boolean> => {
      try {
        await dispatch(consumeFeatureAction({ featureKey, usageData })).unwrap();
        return true;
      } catch (error) {
        console.error('Failed to use feature:', error);
        return false;
      }
    },
    [dispatch]
  );

  // Get feature cost
  const getFeatureCost = useCallback(
    (featureKey: string): number => {
      const feature = creditsState.availableFeatures.find(f => f.featureKey === featureKey);
      return feature?.requiredCredits || 0;
    },
    [creditsState.availableFeatures]
  );

  // Check if user has sufficient credits for a feature
  const hasSufficientCredits = useCallback(
    (featureKey: string): boolean => {
      const cost = getFeatureCost(featureKey);
      return (creditsState.balance?.availableCredits || 0) >= cost;
    },
    [creditsState.balance, getFeatureCost]
  );

  // Alert management
  const addCreditAlert = useCallback(
    (alert: Omit<CreditAlert, 'id' | 'timestamp'>) => {
      const fullAlert: CreditAlert = {
        ...alert,
        id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
      };
      dispatch(addAlert(fullAlert));
    },
    [dispatch]
  );

  const markAlertRead = useCallback(
    (alertId: string) => {
      dispatch(markAlertAsRead(alertId));
    },
    [dispatch]
  );

  const markAllAlertsRead = useCallback(() => {
    dispatch(markAllAlertsAsRead());
  }, [dispatch]);

  const removeAlertById = useCallback(
    (alertId: string) => {
      dispatch(removeAlert(alertId));
    },
    [dispatch]
  );

  const clearAllAlerts = useCallback(() => {
    dispatch(clearAlerts());
  }, [dispatch]);

  // Get unread alerts count
  const unreadAlertsCount = creditsState.alerts.filter(alert => !alert.isRead).length;

  // Get recent alerts (last 5)
  const recentAlerts = creditsState.alerts.slice(0, 5);

  // Format credits for display
  const formatCredits = useCallback((credits: number): string => {
    return credits.toLocaleString();
  }, []);

  // Get credit status (good, low, critical)
  const getCreditStatus = useCallback((): 'good' | 'low' | 'critical' => {
    const available = creditsState.balance?.availableCredits || 0;
    if (available <= 5) return 'critical';
    if (available <= 20) return 'low';
    return 'good';
  }, [creditsState.balance]);

  // Get status color
  const getStatusColor = useCallback((): string => {
    const status = getCreditStatus();
    switch (status) {
      case 'critical': return '#ef4444'; // red-500
      case 'low': return '#f59e0b'; // amber-500
      case 'good': return '#10b981'; // emerald-500
      default: return '#6b7280'; // gray-500
    }
  }, [getCreditStatus]);

  return {
    // State
    balance: creditsState.balance,
    transactions: creditsState.transactions,
    availableFeatures: creditsState.availableFeatures,
    alerts: creditsState.alerts,
    isLoading: creditsState.isLoading,
    error: creditsState.error,
    lastBalanceUpdate: creditsState.lastBalanceUpdate,

    // Actions
    initializeCredits,
    refreshCredits,
    refreshAllCredits,
    canAccessFeature,
    consumeFeature,
    getFeatureCost,
    hasSufficientCredits,

    // Alert management
    addCreditAlert,
    markAlertRead,
    markAllAlertsRead,
    removeAlertById,
    clearAllAlerts,
    unreadAlertsCount,
    recentAlerts,

    // Utilities
    formatCredits,
    getCreditStatus,
    getStatusColor,

    // Redux actions (for advanced usage)
    dispatch,
    clearError: () => dispatch(clearError()),
    updateBalance: (balance: any) => dispatch(updateBalance(balance)),
  };
};